import { FX_ONE, perTick } from '../engine/simulation/fixed';
import { ALL_TECHS, Cat, KINDS, civDef } from './registry';
import { C, RES_COUNT, Res, type Cost, type Effect, type TechDef } from './types';

export const TICK_RATE = 20;

/** Per-player effective statistics for one entity kind, in simulation units. */
export interface KindStats {
  hp: number;
  meleeAttack: number;
  pierceAttack: number;
  meleeArmor: number;
  pierceArmor: number;
  /** Range in fixed-point tiles. */
  range: number;
  minRange: number;
  /** LOS in whole tiles. */
  los: number;
  /** Fixed-point tiles per tick. */
  speed: number;
  /** Ticks between attacks. */
  cooldown: number;
  /** Ticks to train. */
  trainTicks: number;
  /** Villager-ticks to build. */
  buildTicks: number;
  cost: Cost;
  /** Fixed-point resource units gathered per tick, by resource. */
  gather: [number, number, number, number];
  huntGather: number;
  carry: number;
  accuracy: number;
  bonusDmgPct: number;
  /** Projectiles per volley (towers gain extra arrows with age). */
  arrows: number;
  classes: number;
  enabled: boolean;
}

export interface TechStats {
  cost: Cost;
  ticks: number;
  enabled: boolean;
}

interface Accum {
  hp: number; hpPct: number; meleeAttack: number; pierceAttack: number; meleeArmor: number; pierceArmor: number;
  range: number; los: number; speedPct: number; trainTimePct: number; costPct: number; gatherPct: number[];
  huntPct: number; buildHpPct: number; attackSpeedPct: number; carry: number; accuracy: number; bonusDmgPct: number; extraArrows: number;
}

function emptyAccum(): Accum {
  return {
    hp: 0, hpPct: 0, meleeAttack: 0, pierceAttack: 0, meleeArmor: 0, pierceArmor: 0, range: 0, los: 0,
    speedPct: 0, trainTimePct: 0, costPct: 0, gatherPct: [0, 0, 0, 0], huntPct: 0, buildHpPct: 0,
    attackSpeedPct: 0, carry: 0, accuracy: 0, bonusDmgPct: 0, extraArrows: 0,
  };
}

function matches(e: Effect, key: string, classes: number): boolean {
  if (e.keys) return e.keys.includes(key);
  if (e.classes !== undefined) return (e.classes & classes) !== 0;
  return true;
}

/** Global (non-kind) player modifiers. */
export interface GlobalStats {
  farmFood: number;
  tradePct: number;
  researchTimePct: number;
  buildTimePct: number;
}

/**
 * Compute effective stats for every kind given a civ and set of researched techs.
 * Called whenever a player's tech state changes; pure and deterministic.
 */
/**
 * `rules` is the simulation rules version (see SIM_VERSION): before v8 villagers struck for 3, not 4.
 */
export function computeStats(civ: number, researched: Uint8Array, rules = Infinity): { kinds: KindStats[]; techs: TechStats[]; global: GlobalStats } {
  const cdef = civDef(civ);
  const effects: Effect[] = [...cdef.effects];
  for (let i = 0; i < ALL_TECHS.length; i++) if (researched[i]) effects.push(...ALL_TECHS[i].effects);

  const global: GlobalStats = { farmFood: 0, tradePct: 0, researchTimePct: 0, buildTimePct: 0 };
  for (const e of effects) {
    if (e.stat === 'farmFood') global.farmFood += e.value;
    else if (e.stat === 'tradePct') global.tradePct += e.value;
    else if (e.stat === 'researchTimePct') global.researchTimePct += e.value;
    else if (e.stat === 'buildTimePct') global.buildTimePct += e.value;
  }

  const disabled = new Set(cdef.disabled);
  const kinds: KindStats[] = KINDS.map((k) => {
    const u = k.unit;
    const b = k.building;
    const classes = u ? u.classes : b ? b.classes : 0;
    const a = emptyAccum();
    for (const e of effects) {
      if (!matches(e, k.key, classes)) continue;
      switch (e.stat) {
        case 'gatherPct':
          if (e.res === undefined) for (let r = 0; r < RES_COUNT; r++) a.gatherPct[r] += e.value;
          else a.gatherPct[e.res] += e.value;
          break;
        case 'farmFood': case 'tradePct': case 'researchTimePct': case 'buildTimePct':
          break;
        default:
          (a as unknown as Record<string, number>)[e.stat] += e.value;
      }
    }
    const atk = u?.attack ?? b?.attack;
    const baseHp = u ? u.hp : b ? b.hp : 1;
    const hpPct = u ? a.hpPct : a.buildHpPct;
    const hp = Math.max(1, Math.trunc(((baseHp + a.hp) * (100 + hpPct)) / 100));
    const baseCost: Cost = u ? u.cost : b ? b.cost : [0, 0, 0, 0];
    const cost = baseCost.map((v) => Math.max(0, Math.trunc((v * (100 + a.costPct)) / 100))) as Cost;
    const rates = u?.gatherRates ?? [0, 0, 0, 0];
    const gather = rates.map((r, i) => Math.round((((r * FX_ONE) / TICK_RATE) * (100 + a.gatherPct[i])) / 100)) as [number, number, number, number];
    const cooldownSec = atk ? atk.cooldown : 1;
    const cooldown = Math.max(1, Math.round((cooldownSec * TICK_RATE * 100) / (100 + a.attackSpeedPct)));
    const isCat = k.cat;
    const enabled = !disabled.has(k.key) && (!(u?.civ || b?.civ) || (u?.civ ?? b?.civ) === cdef.key);
    return {
      hp,
      meleeAttack: atk ? Math.max(0, atk.melee - (u?.key === 'villager' && rules < 8 ? 1 : 0) + (atk.melee > 0 || u?.classes === C.Villager ? a.meleeAttack : 0)) : 0,
      pierceAttack: atk ? Math.max(0, atk.pierce + (atk.pierce > 0 ? a.pierceAttack : 0)) : 0,
      meleeArmor: (u ? u.armor.melee : b ? b.armor.melee : 0) + a.meleeArmor,
      pierceArmor: (u ? u.armor.pierce : b ? b.armor.pierce : 0) + a.pierceArmor,
      range: atk ? Math.round((atk.range + (atk.range > 0 ? a.range : 0)) * FX_ONE) : 0,
      minRange: atk?.minRange ? Math.round(atk.minRange * FX_ONE) : 0,
      los: (u ? u.los : b ? b.los : 0) + a.los + (atk && atk.range > 0 ? a.range : 0),
      speed: u ? Math.round((perTick(u.speed, TICK_RATE) * (100 + a.speedPct)) / 100) : 0,
      cooldown,
      trainTicks: u ? Math.max(1, Math.round((u.trainTime * TICK_RATE * (100 + a.trainTimePct)) / 100)) : 0,
      buildTicks: b ? Math.max(1, Math.round((b.buildTime * TICK_RATE * (100 + global.buildTimePct)) / 100)) : 0,
      cost,
      gather,
      huntGather: Math.round((gather[Res.Food] * (100 + a.huntPct)) / 100),
      carry: (u?.carryCapacity ?? 0) + a.carry,
      accuracy: Math.min(100, (atk?.accuracy ?? 100) + a.accuracy),
      bonusDmgPct: a.bonusDmgPct,
      arrows: 1 + a.extraArrows,
      classes,
      enabled: isCat === Cat.Resource ? false : enabled,
    };
  });

  const techs: TechStats[] = ALL_TECHS.map((t: TechDef) => {
    let costPct = 0;
    for (const e of cdef.effects) if (e.stat === 'costPct' && e.keys?.includes(t.key)) costPct += e.value;
    const cost = t.cost.map((v) => Math.trunc((v * (100 + costPct)) / 100)) as Cost;
    const ticks = Math.max(1, Math.round((t.time * TICK_RATE * (100 + global.researchTimePct)) / 100));
    const enabled = !t.civ || t.civ === cdef.key;
    return { cost, ticks, enabled };
  });

  return { kinds, techs, global };
}
